import React from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import styles from './styles.module.scss';

interface FormData {
  name: string;
  description: string;
  isPublic: boolean;
}

interface TournamentDetailsStepProps {
  formData: FormData;
  onChange: (field: keyof FormData, value: string | boolean) => void;
  onBack: () => void;
  onSubmit: () => void;
  isAdmin: boolean;
  isLoading: boolean;
  selectedLeagueName: string;
  selectedLeagueLogo: string | null;
  nameError: boolean;
}

const TournamentDetailsStep: React.FC<TournamentDetailsStepProps> = ({
  formData,
  onChange,
  onBack,
  onSubmit,
  isAdmin,
  isLoading,
  selectedLeagueName,
  selectedLeagueLogo,
  nameError,
}) => {
  const { t } = useTranslation(['tournament', 'common']);

  return (
    <>
      <div className={styles.formFields}>
        {/* Selected league badge */}
        <div className={styles.selectedLeagueBadge}>
          {selectedLeagueLogo && (
            <img src={selectedLeagueLogo} alt={selectedLeagueName} />
          )}
          <span>{selectedLeagueName}</span>
        </div>

        {/* Tournament Name */}
        <div className={styles.fieldGroup}>
          <label className={styles.label}>{t('tournament:tournamentName')} *</label>
          <input
            className={classNames('field', { 'field--error': nameError })}
            type="text"
            value={formData.name}
            onChange={(e) => onChange('name', e.target.value)}
            placeholder={t('tournament:namePlaceholder')}
            disabled={isLoading}
            maxLength={100}
          />
          <span className={styles.hint}>{t('tournament:tournamentNameHint')}</span>
        </div>

        {/* Tournament Description */}
        <div className={styles.fieldGroup}>
          <label className={styles.label}>{t('tournament:description')}</label>
          <textarea
            className="field"
            value={formData.description}
            onChange={(e) => onChange('description', e.target.value)}
            placeholder={t('tournament:descriptionPlaceholder')}
            rows={3}
            disabled={isLoading}
            maxLength={500}
          />
          <span className={styles.hint}>{t('tournament:descriptionHint')}</span>
        </div>

        {/* Admin-only settings */}
        {isAdmin && (
          <div className={styles.settingsSection}>
            <h3 className={styles.sectionTitle}>{t('tournament:settings')}</h3>

            {/* Public/Private checkbox */}
            <label className={styles.checkboxOption}>
              <input
                type="checkbox"
                checked={formData.isPublic}
                onChange={(e) => onChange('isPublic', e.target.checked)}
                disabled={isLoading}
              />
              <span className={styles.checkboxLabel}>
                {t('tournament:publicTournamentCheckbox')}
              </span>
            </label>
          </div>
        )}
      </div>

      <div className={styles.navRow}>
        <button
          type="button"
          className="btn btn--outlined"
          onClick={onBack}
          disabled={isLoading}
          data-testid="step-back"
        >
          {t('common:back')}
        </button>
        <button
          type="button"
          className="btn"
          onClick={onSubmit}
          disabled={isLoading || !formData.name.trim() || formData.name.trim().length < 3}
          data-testid="step-submit"
        >
          {isLoading ? t('tournament:creating') : t('tournament:createTournament')}
        </button>
      </div>
    </>
  );
};

export default TournamentDetailsStep;
